const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper function to fetch with authentication
const fetchWithAuth = async (url, options = {}) => {
  // Get token from localStorage (zustand persist)
  const authStorage = localStorage.getItem('auth-storage');
  const token = authStorage ? JSON.parse(authStorage).state.token : null;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized - token expired or invalid
  if (response.status === 401) {
    // Import here to avoid circular dependency
    const { default: useAuthStore } = await import('../../stores/authStore');
    useAuthStore.getState().signout();
    window.location.href = '/signin';
    throw new Error('Session expired. Please sign in again.');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
};

// Posts API
export const getPosts = (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  if (params.search) queryParams.append('search', params.search);
  if (params.searchTitle) queryParams.append('searchTitle', params.searchTitle);
  if (params.searchContent) queryParams.append('searchContent', params.searchContent);
  if (params.category) queryParams.append('category', params.category);
  if (params.userId) queryParams.append('userId', params.userId);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.order) queryParams.append('order', params.order);
  
  return fetchWithAuth(`/api/feed/posts?${queryParams.toString()}`);
};

export const getPostById = (id) => {
  return fetchWithAuth(`/api/feed/posts/${id}`);
};

export const createPost = (data) => {
  return fetchWithAuth('/api/feed/posts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updatePost = (id, data) => {
  return fetchWithAuth(`/api/feed/posts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deletePost = (id) => {
  return fetchWithAuth(`/api/feed/posts/${id}`, {
    method: 'DELETE',
  });
};

export const getMyPosts = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  
  return fetchWithAuth(`/api/feed/posts/my/posts?${queryParams.toString()}`);
};

export const canPostToday = () => {
  return fetchWithAuth('/api/feed/posts/my/can-post-today');
};

// Reactions API
export const addReaction = (postId, reactionType) => {
  return fetchWithAuth(`/api/feed/posts/${postId}/reactions`, {
    method: 'POST',
    body: JSON.stringify({ reactionType }),
  });
};

export const removeReaction = (postId) => {
  return fetchWithAuth(`/api/feed/posts/${postId}/reactions`, {
    method: 'DELETE',
  });
};

export const getReactions = (postId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  
  return fetchWithAuth(`/api/feed/posts/${postId}/reactions?${queryParams.toString()}`);
};

// Comments API
export const getComments = (postId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  
  return fetchWithAuth(`/api/feed/posts/${postId}/comments?${queryParams.toString()}`);
};

export const addComment = (postId, content) => {
  return fetchWithAuth(`/api/feed/posts/${postId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
};

export const updateComment = (commentId, content) => {
  return fetchWithAuth(`/api/feed/posts/comments/${commentId}`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
};

export const deleteComment = (commentId) => {
  return fetchWithAuth(`/api/feed/posts/comments/${commentId}`, {
    method: 'DELETE',
  });
};

// Notifications API
export const getNotifications = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  if (params.unreadOnly) queryParams.append('unreadOnly', params.unreadOnly);
  
  return fetchWithAuth(`/api/feed/notifications?${queryParams.toString()}`);
};

export const getUnreadCount = () => {
  return fetchWithAuth('/api/feed/notifications/unread-count');
};

export const markNotificationAsRead = (id) => {
  return fetchWithAuth(`/api/feed/notifications/${id}/read`, {
    method: 'PUT',
  });
};

export const markAllNotificationsAsRead = () => {
  return fetchWithAuth('/api/feed/notifications/read-all', {
    method: 'PUT',
  });
};

// Upload API
export const uploadImage = async (file) => {
  const authStorage = localStorage.getItem('auth-storage');
  const token = authStorage ? JSON.parse(authStorage).state.token : null;

  const formData = new FormData();
  formData.append('image', file);

  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}/api/feed/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  // Handle 401 Unauthorized - token expired or invalid
  if (response.status === 401) {
    const { default: useAuthStore } = await import('../../stores/authStore');
    useAuthStore.getState().signout();
    window.location.href = '/signin';
    throw new Error('Session expired. Please sign in again.');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Upload failed');
  }

  return data;
};

// Moderation API
export const getModerationQueue = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  
  return fetchWithAuth(`/api/feed/moderation/queue?${queryParams.toString()}`);
};

export const approvePost = (postId) => {
  return fetchWithAuth(`/api/feed/moderation/posts/${postId}/approve`, {
    method: 'POST',
  });
};

export const rejectPost = (postId) => {
  return fetchWithAuth(`/api/feed/moderation/posts/${postId}/reject`, {
    method: 'POST',
  });
};

export const deleteAnyPost = (postId) => {
  return fetchWithAuth(`/api/feed/moderation/posts/${postId}`, {
    method: 'DELETE',
  });
};

// Aliases for consistency
export const markAsRead = markNotificationAsRead;
export const markAllAsRead = markAllNotificationsAsRead;
